import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { resolveBack } from '../../utils/backOrigin';
import InvoicesTab from './InvoicesTab';
import {
  PlusIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import BackLink from '../ui/BackLink';
import { Card, CardHeader, CardBody } from '../ui/Card';
import { Checkbox } from '../ui/Checkbox';
import StatusPill, { SignaturePill, humanizeEnum } from '../ui/StatusPill';
import useTableSort from '../ui/useTableSort';
import { formatDate } from '../../utils/formatDate';
import { coStatusLabel, coStatusTone, coName, coNumber, isCoDraftLike, isCoApprovedNotAdded } from '../changeorders/coDisplay';
import { formatJobNumber, formatQuoteNumber, workOrderLabel } from '../../utils/displayId';
import { getLineItemName } from '../../utils/canonicalFields';
import { formatCurrency } from '../../utils/formatCurrency';
import { getJobContractTotal } from '../../utils/jobContractTotal';
import { summarizeChangeOrder, coLines } from '../../change-order/coPrice';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { Alert } from '../ui/Alert';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonCard } from '../ui/Skeleton';
import DetailPageHeader from '../ui/DetailPageHeader';
import Tabs from '../ui/Tabs';
import AcknowledgmentsPanel from '../acknowledgments/AcknowledgmentsPanel';
import BulkActionBar from '../ui/BulkActionBar';
import DropdownMenu from '../ui/DropdownMenu';
import { deriveJobStatus } from '../../utils/jobStatus';
import { LockedAtAcceptancePill } from '../quotes/LockedAtAcceptancePill';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isFieldTech as isFieldTechRule } from '../../utils/isFieldTech';
import { useConfirm } from '../ui/ConfirmationModal';
import { confirmAndDeleteWO } from '../../utils/woDelete';
import { useJobCompletion, apiErrorMessage } from './JobCompletionDialog';
import { isJobCompleted } from '../../utils/jobCompletion';
import { WOCreateSheet } from '../workorders/WOCreateSheets';
import { AttachModal } from '../attachments/AttachModal';
import { isEnabled } from '../../config/flags';
import { entryDisplay, formatBytes, isAttachmentRef, removeDocumentById } from '../../utils/attachmentEntry';
import FilePreviewModal from '../attachments/FilePreviewModal';
import { invoiceFileLabel } from '../invoices/invoiceFiles';
import { useSettingsList } from '../../hooks/useSettingsList';
import { COCreateSheet } from '../changeorders/ChangeOrderComponents';
import JobCostingTab from './JobCostingTab';
import useSchedulingDefaults from '../../hooks/useSchedulingDefaults';
import useTechOptions from '../../hooks/useTechOptions';
import GoldenCustomerCard from '../ui/GoldenCustomerCard';
import { NotesPanel } from '../ui/NotesPanel';
import { mergeJobNotes } from '../../utils/jobNotes';
import { useToast } from '../ui/Toast';
import { noteActionErrorMessage } from '../../quote-builder/quoteInternalNotes';
import { useSettingsContext } from '../../hooks/useSettings';
import SharedWorkOrderAccordion from '../shared/WorkOrderAccordion';
import { getJobSiteAddress } from '../../utils/customerAddress';
import { lineSqFt } from '../../utils/lineQuantity';

// ─── Tab Definitions ─────────────────────────────────────────────────────────

const TABS = [
  // PT-19 (2026-07-24) 19.1: order per JOB-1 §172. Primitive uses `value` (not `id`).
  // `dot: <boolean>` supported by the Tabs atom (PT-19 W1 primitive extension) — wired
  // for change-orders based on `pendingCOs > 0` at render time.
  { value: 'work-orders',   label: 'Work Orders',    icon: ClipboardDocumentListIcon },
  { value: 'change-orders', label: 'Change Orders',  icon: DocumentTextIcon },
  { value: 'costing',       label: 'Job Costing',    icon: CurrencyDollarIcon },
  { value: 'invoices',      label: 'Invoices',       icon: BanknotesIcon },
  { value: 'notes',         label: 'Notes',          icon: ChatBubbleLeftRightIcon },
  { value: 'documents',     label: 'Files',          icon: FolderIcon },
  // IDI-542: the quote's acknowledgment rows plus its change orders' (B-17).
  { value: 'acknowledgements', label: 'Acknowledgments', icon: ShieldCheckIcon },
];

// IDI-523: the only tabs a field tech sees (no money surfaces). Any other tab
// asked for (?tab=, location.state.tab) opens Work Orders instead.
export const FIELD_TECH_TABS = ['work-orders', 'notes', 'documents'];

// ─── Work Orders Tab ─────────────────────────────────────────────────────────


// GP-07 / PR-7: uses shared WorkOrderAccordion.
function WorkOrdersTab({ workOrders, jobId, onCreateWO, isFieldTech, crews, canDelete, onDeleted, onError }) {
  const confirm = useConfirm();
  // IDI-469: row ⋮ Delete — paid roles only (B-03); the job reloads in place.
  const handleDeleteWO = async (wo) => {
    try {
      if (await confirmAndDeleteWO({ confirm, wo, jobId })) onDeleted();
    } catch (err) {
      onError(err?.message || 'Failed to delete work order');
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {(workOrders || []).length} work order{(workOrders || []).length !== 1 ? 's' : ''}
        </p>
        {!isFieldTech && (
          <Button variant="secondary" size="sm" onClick={onCreateWO}>
            <PlusIcon className="h-4 w-4 mr-1" />
            Create Work Order
          </Button>
        )}
      </div>
      <Card className="p-3 mb-6">
        {/* IDI-315 E1 (2026-09-12): missing mb-6 — QuoteLineItemsPanel below
            uses `className="mb-6"` for card-to-card spacing; this card had
            only `p-3` (inner padding), so the gap between this card and the
            Quote Items card below it was exactly 0px. */}
        <SharedWorkOrderAccordion
          workOrders={workOrders}
          jobId={jobId}
          crews={crews}
          onDeleteWO={canDelete ? handleDeleteWO : undefined}
        />
      </Card>
    </div>
  );
}


// IDI-479: Change Orders click-to-sort. Net Change is signed cents from the
// same shared reader the cell uses, so negatives sort before positives.
const CHANGE_ORDER_SORT_COLUMNS = [
  { key: 'name', type: 'text', value: (co) => coName(co) || coNumber(co) || null },
  {
    key: 'net',
    type: 'money',
    value: (co) => {
      const lines = coLines(co);
      return lines.length === 0 ? null : summarizeChangeOrder(lines).netCents;
    },
  },
  { key: 'acknowledgments', type: 'text', value: (co) => humanizeEnum(co.signatureStatus || co.signature_status || '') || null },
  { key: 'status', type: 'text', value: (co) => coStatusLabel((co.status || '').toLowerCase()) || null },
  { key: 'date', type: 'date', value: (co) => co.createdAt || co.created_at || null },
];

export function ChangeOrdersTab({ changeOrders, jobId, onCreateCO }) {
  const coNavigate = useNavigate();
  const { sortedRows: sortedChangeOrders, getSortProps } = useTableSort(changeOrders || [], CHANGE_ORDER_SORT_COLUMNS);

  if (!changeOrders || changeOrders.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-gray-900 mb-1">No change orders</h3>
        <p className="text-sm text-gray-500 mb-4">
          Change orders capture scope changes after the original quote was accepted.
        </p>
        <Button variant="secondary" onClick={onCreateCO}>
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Create Change Order
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {changeOrders.length} change order{changeOrders.length !== 1 ? 's' : ''}
        </p>
        <Button variant="secondary" size="sm" onClick={onCreateCO}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Create Change Order
        </Button>
      </div>
      {/* IDI-556: this table follows the Quotes table — name over number,
          Net Change, Acknowledgments, Status, Date. No total-price column,
          chips or row menu. Labels and title come from coDisplay. */}
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader {...getSortProps('name')}>Change Order</TableHeader>
            <TableHeader align="right" {...getSortProps('net')}>Net Change</TableHeader>
            <TableHeader {...getSortProps('acknowledgments')}>Acknowledgments</TableHeader>
            <TableHeader {...getSortProps('status')}>Status</TableHeader>
            <TableHeader {...getSortProps('date')}>Date</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedChangeOrders.map((co) => {
            const status = (co.status || '').toLowerCase();
            const sigStatus = co.signatureStatus || co.signature_status || '';
            const number = coNumber(co);
            const created = co.createdAt || co.created_at;
            return (
            <TableRow
              key={co.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => {
                // §7 (2026-08-08): route by status. Draft-like (draft, an old
                // withdrawn CO — IDI-556 — or none) and active → edit in the
                // CO builder; everything else → the read-only detail.
                if (isCoDraftLike(status) || status === 'active') {
                  coNavigate(`/jobs/${jobId}/change-orders/${co.id}/build`);
                } else {
                  coNavigate(`/change-orders/${co.id}`);
                }
              }}
            >
              <TableCell className="font-semibold">
                {coName(co) || (number ? 'Change Order' : '—')}
                {number && (
                  <span className="block text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>{number}</span>
                )}
              </TableCell>
              <TableCell align="right">
                {(() => {
                  // IDI-492: the ONE shared reader, so the job list shows the
                  // net the server applies and invoices (never a stored net).
                  const lines = coLines(co);
                  if (lines.length === 0) return "—";
                  const s = summarizeChangeOrder(lines);
                  return (
                    <span className="inline-flex items-center gap-2">
                      {formatCurrency(s.netCents)}
                      {s.needsRepricing && <StatusPill status="needs_repricing" tone="warning" label="Needs re-pricing" size="sm" />}
                    </span>
                  );
                })()}
              </TableCell>
              <TableCell>
                {sigStatus ? <SignaturePill signatureStatus={sigStatus} size="sm" /> : '—'}
              </TableCell>
              <TableCell>
                <StatusPill
                  status={status || 'draft'}
                  label={coStatusLabel(status)}
                  tone={coStatusTone(status)}
                />
                {/* IDI-556 / IDI-560: an Approved CO only counts toward the
                    job's total once it is added to the job. */}
                {isCoApprovedNotAdded(co) && (
                  <span className="block text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Not added to job</span>
                )}
              </TableCell>
              <TableCell className="text-gray-500">
                {created ? formatDate(created) || '—' : '—'}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Notes Tab ───────────────────────────────────────────────────────────────

// IDI-551: the job's own notes plus its converted quote's notes (merged by
// utils/jobNotes, newest first). A quote note is labelled "Quote Q-…" with no
// link (IDI-157). Work-order notes show on their work order only.
// IDI-552: on a converted job, a quote note's AUTHOR edits or deletes it here
// (Adrian 10-21) — routed to the quote's endpoints.
// IDI-553: the job's own notes are edited/deleted by their author (any role)
// via the job's edit_note/delete_note actions. A row with no author_id
// (legacy "You"/Unknown) gets no buttons (NotesPanel's author check).
export function NotesTab({
  notes, onAddNote, onEditQuoteNote, onDeleteQuoteNote,
  onEditJobNote, onDeleteJobNote, currentUserId,
}) {
  const mappedNotes = (notes || []).map(n => ({
    id: n.id,
    content: n.content || n.text,
    author: n.author,
    author_id: n.author_id || n.authorId,
    created_at: n.created_at || n.createdAt,
    updated_at: n.updated_at || n.updatedAt,
    ...(n.source === 'quote'
      ? { sourceLabel: n.sourceLabel, source: 'quote', noteId: n.noteId, quoteId: n.quoteId }
      : { source: n.source || 'job' }),
  }));

  const onEdit = (note, content) => (note?.source === 'quote'
    ? onEditQuoteNote?.(note, content)
    : onEditJobNote?.(note, content));
  const onDelete = (note) => (note?.source === 'quote'
    ? onDeleteQuoteNote?.(note)
    : onDeleteJobNote?.(note));

  return (
    <NotesPanel
      notes={mappedNotes}
      onAdd={onAddNote}
      onEdit={onEdit}
      onDelete={onDelete}
      currentUserId={currentUserId}
      canModify={(n) => (isEditableQuoteRow(n) && !!onEditQuoteNote)
        || (isEditableJobRow(n) && !!onEditJobNote)}
    />
  );
}

// IDI-552: only quote-sourced rows with a known quote are editable here.
function isEditableQuoteRow(n) {
  return n?.source === 'quote' && !!n.noteId && !!n.quoteId;
}

// IDI-553: a job row with a stored id (synthesised `job-note-N` ids name no
// stored note and are never editable).
function isEditableJobRow(n) {
  return n?.source === 'job' && !!n.id && !/^job-note-\d+$/.test(n.id);
}

// ─── Files Tab ──────────────────────────────────────────────────
// FP2-F5 terminology (grounding §7, 2026-07-25): 'Files' = browsable store
// (this Job's tab, Settings Files repo). 'Attachments' = files bound to a
// specific record (WO/Quote/Invoice/CO). Same 'Attachment' entity (E-017);
// the label follows the user's relationship to the record, not the storage.
// Do not "correct" the split — the WO create sheet's Attachments field and
// this Job Files tab surface the same underlying records with intentionally
// different labels.───────────

// IDI-484 (Q-45): files attached from an invoice are stored on the job with
// sourceType="invoice"; the Files tab labels each one with its invoice
// number. The invoice list is fetched only when the job HAS invoice files.
// IDI-582 S4: a field tech can't read invoices (the API refuses), so the
// list is not fetched and invoice files read "Invoice".
function useInvoicesById(jobId, documents, skip = false) {
  const hasInvoiceFiles = !skip && Array.isArray(documents) && documents.some((d) => d && d.sourceType === 'invoice');
  const [byId, setById] = useState({});
  useEffect(() => {
    if (!hasInvoiceFiles || !jobId) return undefined;
    let cancelled = false;
    api.getInvoices({ job_id: jobId })
      .then((res) => {
        if (cancelled) return;
        const all = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
        const map = {};
        all.forEach((inv) => { if (inv && inv.id) map[inv.id] = inv; });
        setById(map);
      })
      .catch(() => { /* label falls back to "Invoice" */ });
    return () => { cancelled = true; };
  }, [hasInvoiceFiles, jobId]);
  return byId;
}

// IDI-55 (BUILD CONTRACT 2026-10-21): the job's Files tab lists, beside the
// job's own files (and its invoice files, IDI-484), the files added while its
// quote was built (job GET `quoteDocuments`, read at view time, never copied)
// and the files on its work orders and change orders. Each row carries its
// source. The job's own, invoice and converted-quote rows are the job's:
// Delete is offered on them (Adrian 10-21). WO and CO rows link to their
// source and are never deleted here (Q-47). Quote rows have no link (IDI-157
// redirects a converted quote to its job).
export function buildJobFileRows({
  documents, quoteDocuments, workOrders, changeOrders, quoteNumber, invoicesById = {}, jobId,
} = {}) {
  const rows = [];
  const asList = (v) => (Array.isArray(v) ? v : []);
  asList(documents).forEach((doc) => {
    if (!doc) return;
    const isInvoice = doc.sourceType === 'invoice';
    rows.push({
      doc,
      sourceKind: isInvoice ? 'invoice' : 'job',
      sourceLabel: isInvoice ? invoiceFileLabel(doc, invoicesById) : null,
      sourceHref: null,
      deletable: true,
    });
  });
  const quoteLabel = quoteNumber
    ? `Quote ${formatQuoteNumber({ quoteNumber })}`
    : 'Quote';
  asList(quoteDocuments).forEach((doc) => {
    if (!doc) return;
    rows.push({ doc, sourceKind: 'quote', sourceLabel: quoteLabel, sourceHref: null, deletable: true });
  });
  asList(workOrders).forEach((wo) => {
    if (!wo) return;
    const label = workOrderLabel(wo);
    asList(wo.attachments).forEach((doc) => {
      if (!doc) return;
      rows.push({
        doc,
        sourceKind: 'workOrder',
        sourceLabel: label === 'Work order' ? 'Work order' : `Work order ${label}`,
        sourceHref: wo.id
          ? `/work-orders/${encodeURIComponent(wo.id)}${jobId ? `?job=${encodeURIComponent(jobId)}` : ''}`
          : null,
        deletable: false,
      });
    });
  });
  asList(changeOrders).forEach((co) => {
    if (!co) return;
    const number = coNumber(co);
    asList(co.documents).forEach((doc) => {
      if (!doc) return;
      rows.push({
        doc,
        sourceKind: 'changeOrder',
        sourceLabel: number ? `Change order ${number}` : 'Change order',
        sourceHref: co.id ? `/change-orders/${encodeURIComponent(co.id)}` : null,
        deletable: false,
      });
    });
  });
  return rows;
}

// IDI-55: Delete on a job, invoice or quote row — removable refs only (an
// AttachmentRef that is not a library reference). A paid seat removes any; a
// field tech only a file they added (IDI-453 10-20; the server enforces it).
export function canDeleteJobFileRow(row, { isFieldTech, userId } = {}) {
  if (!row || !row.deletable) return false;
  const doc = row.doc;
  if (!isAttachmentRef(doc) || doc.source === 'library') return false;
  if (!isFieldTech) return true;
  const author = doc.authorUserId || doc.author_user_id;
  return !!author && !!userId && author === userId;
}

function FilesTab({
  jobId, documents, quoteDocuments, workOrders, changeOrders, quoteNumber,
  onUpload, attachEnabled, isFieldTech, userId, onDeleted,
}) {
  const invoicesById = useInvoicesById(jobId, documents, !!isFieldTech);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [deleteError, setDeleteError] = useState(null);
  // IDI-445: the row whose file is open in the preview modal.
  const [previewRow, setPreviewRow] = useState(null);
  const rows = buildJobFileRows({
    documents, quoteDocuments, workOrders, changeOrders, quoteNumber, invoicesById, jobId,
  });
  const handleDelete = async (row) => {
    const v = entryDisplay(row.doc);
    const ok = await confirm({
      title: 'Delete file?',
      message: `"${v.name}" will be removed${row.sourceKind === 'quote' ? ' from this job and its quote' : ''}.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      setDeleteError(null);
      await api.deleteAttachment({ attachmentId: row.doc.attachmentId });
      onDeleted(row);
    } catch (err) {
      setDeleteError(err?.friendlyMessage || `Failed to delete file: ${err?.message || 'unknown error'}`);
    }
  };
  // PT-19 19.8 (2026-07-24): endpoint E2 (presigned upload for JOB documents) was
  // MISSING at ship. PT-53b commit 1 (2026-08-02) wires AttachModal behind the
  // FLAGS.ATTACHMENTS_UPLOAD gate; the disabled/honest-copy shape stays as-is
  // until the flag flips (S3_BUCKET_FILES on the deployed Lambda). See
  // FILES_SCOPE_AND_VISIBILITY.md §1 (job is one of five upload points) + §4a.
  //
  // Do NOT wire onClick to a local File state that silently drops on reload.
  const UPLOAD_PENDING_COPY = 'Upload coming soon — attachment endpoint pending.';
  const canUpload = attachEnabled === true;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {rows.length} document{rows.length !== 1 ? 's' : ''}
        </p>
        <Button
          variant="secondary"
          size="sm"
          disabled={!canUpload}
          onClick={canUpload ? onUpload : undefined}
          title={canUpload ? 'Attach a file to this job' : UPLOAD_PENDING_COPY}
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Upload
        </Button>
      </div>

      {deleteError && (
        <div className="mb-3"><Alert variant="error">{deleteError}</Alert></div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-12">
          <FolderIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">No documents</h3>
          <p className="text-sm text-gray-500 mb-4">
            {canUpload
              ? 'Attach a file to get started.'
              : UPLOAD_PENDING_COPY}
          </p>
          <Button
            variant="secondary"
            disabled={!canUpload}
            onClick={canUpload ? onUpload : undefined}
            title={canUpload ? 'Attach a file to this job' : UPLOAD_PENDING_COPY}
          >
            Upload Document
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* PT-53b commit 1: entries are a union of legacy inline documents
            * (Shape A: {id, name, size, url, uploaded_at, ...}) and new
            * AttachmentRefs (Shape B: {attachmentId, source, filename, ...}).
            * `entryDisplay` normalises both into a common view shape
            * (see utils/attachmentEntry.js). Branching is deliberate + testable
            * there, not inferred here. */}
          {rows.map((row, idx) => {
            const v = entryDisplay(row.doc);
            const deletable = canDeleteJobFileRow(row, { isFieldTech, userId });
            return (
              <Card
                key={`${row.sourceKind}-${v.key}-${idx}`}
                data-testid="job-file-row"
                onClick={() => setPreviewRow(row)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FolderIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {v.name}
                      {/* R-PROV: library-sourced files display source label. */}
                      {v.source === 'library' && (
                        <span className="ml-2 text-xs font-normal text-gray-500">Library</span>
                      )}
                      {row.sourceLabel && (
                        <span className="ml-2 text-xs font-normal text-gray-500" data-testid="job-file-source">{row.sourceLabel}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatBytes(v.bytes)} •{' '}
                      {v.uploadedAt
                        ? new Date(v.uploadedAt).toLocaleDateString()
                        : '—'}
                    </p>
                  </div>
                  {row.sourceHref && (
                    <button
                      type="button"
                      className="text-xs font-medium text-primary-600 hover:underline flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(row.sourceHref, row.sourceKind === 'workOrder' ? { state: { jobId } } : undefined);
                      }}
                    >
                      Open source
                    </button>
                  )}
                  {deletable && (
                    <button
                      type="button"
                      className="text-xs font-medium text-red-600 hover:underline flex-shrink-0"
                      aria-label={`Delete ${v.name}`}
                      onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {/* IDI-445 D2: Delete per canDeleteJobFileRow (same confirm as the row);
        * work-order / change-order rows offer Open source instead. */}
      {previewRow && (
        <FilePreviewModal
          entry={previewRow.doc}
          onClose={() => setPreviewRow(null)}
          onDelete={canDeleteJobFileRow(previewRow, { isFieldTech, userId })
            ? () => { const r = previewRow; setPreviewRow(null); return handleDelete(r); }
            : undefined}
          onOpenSource={previewRow.sourceHref
            ? () => {
              const r = previewRow;
              setPreviewRow(null);
              navigate(r.sourceHref, r.sourceKind === 'workOrder' ? { state: { jobId } } : undefined);
            }
            : undefined}
        />
      )}
    </div>
  );
}

// ─── Quote Line Items Panel ──────────────────────────────────────────────────

export function QuoteLineItemsPanel({ lineItems, selectedItems, onToggleItem, onToggleAll, coverageMap = {} }) {
  // IDI-315 E2 (2026-09-12): group rows by AREA — the table had a "Sub-area"
  // column but nothing grouping by area at all, so two items in different
  // areas sharing a sub-area name (or two areas with the same-named
  // sub-area) were indistinguishable, and a job with several areas showed
  // one long undifferentiated list. Built against QuoteDocument.js's own
  // groupByAreaOrPassthrough — the customer-facing quote PDF's grouping
  // mechanism for the exact same line-item shape — rather than re-invented:
  // group by areaId/areaLabel at the TOP level; sub-area stays a per-row
  // column WITHIN each area group, not a second nesting level. Confirmed via
  // that file (QuoteDocument.js:50-63, :215-263) this is the existing
  // pattern for this exact ambiguity, not a guess. NOTE: the live tenant
  // checked this session did not have any job with repeated sub-areas
  // within one area, so that specific sub-case (two sub-area rows under one
  // area header) was not observable there — this groups against the
  // reader's own logic (same key resolution as QuoteDocument.js), not
  // against what that one tenant's data happened to show.
  //
  // Hooks-order note: this useMemo MUST run before the early-return below
  // (React's rules-of-hooks forbid a conditional hook call) — it's placed
  // ahead of the empty-state check even though its result is unused on
  // that branch.
  const areaGroups = useMemo(() => {
    const groups = new Map();
    const order = [];
    for (const item of (lineItems || [])) {
      const key = item.areaId || item.area_id || item.areaLabel || item.area_label || '__uncategorized__';
      if (!groups.has(key)) {
        groups.set(key, {
          areaLabel: item.areaLabel || item.area_label || null,
          items: [],
        });
        order.push(key);
      }
      groups.get(key).items.push(item);
    }
    return order.map((k) => groups.get(k));
  }, [lineItems]);

  // DEF-089 (W1.3): previous behaviour was `return null` when lineItems was
  // empty — a silent hide. A job with zero inherited quote line items would
  // simply have this whole panel disappear without acknowledgement, leaving
  // the contractor unable to distinguish "correctly no items" from "panel
  // failed to render". Replace with canonical EmptyState + explanatory
  // sub-copy per response §7.
  //
  // Per response §7: NO inconsistency notice — the panel doesn't render
  // totals, so there's no possible contradiction between totals and
  // line-item count. EmptyState only.
  if (!lineItems || lineItems.length === 0) {
    return (
      <div data-testid="job-detail-line-items-empty">
        <EmptyState
          mode="first-run"
          title="No line items on this job."
          description="Line items are inherited from the linked quote."
        />
      </div>
    );
  }

  // R20-7 (corrected): a line item may appear on any number of WOs.
  // Select-all toggles ALL items (no exclusion for covered rows).
  const selectableIds = lineItems
    .map((item, idx) => item.id || `item-${idx}`);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedItems.includes(id));
  const someSelected = selectedItems.length > 0 && !allSelected;

  // PT-19 W2 19.3–19.5 rework. Per JOB-1 §168 wire + JOB-08 (drop Status +
  // WO-coverage cols) + JOB-02 (post-conversion scope lock — no per-row menu;
  // scope changes flow through the Change Order surface).
  // IDI-184 (2026-10-21): Columns: [✓] · Area · Sub-area · Product · Sq Ft.
  // No line price for any role — a job shows only the contract total (IDI-478).

  return (
    <Card className="mb-6">
      <CardHeader>
        {/* PT-19 19.3: title renamed to "Quote Items" per JOB-1 §168. */}
        <span className="text-sm font-medium text-gray-700">Quote Items</span>
        {selectedItems.length > 0 && (
          <span className="text-xs text-red-600 font-medium">{selectedItems.length} selected</span>
        )}
      </CardHeader>
      <CardBody className="p-0">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-10">
                {/* PT-19 19.4: raw <input> → Checkbox atom (boolean-first onChange). */}
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onToggleAll}
                  aria-label="Select all quote items"
                />
              </TableHeader>
              <TableHeader>Area</TableHeader>
              <TableHeader>Sub-area</TableHeader>
              <TableHeader>Product</TableHeader>
              <TableHeader align="right">Sq Ft</TableHeader>
              {/* PT-19 19.5 (JOB-02 scope lock): NO per-row actions column. */}
            </TableRow>
          </TableHead>
          <TableBody>
            {areaGroups.map((group, groupIdx) => (
              <React.Fragment key={group.areaLabel || `uncategorized-${groupIdx}`}>
                {/* IDI-315 E2: area header row — same qd-areahdr-style separator
                    pattern as QuoteDocument.js's group.areaLabel row, adapted to
                    this table's column count (colSpan 5: checkbox, area,
                    sub-area, product, sq ft — IDI-184). Only rendered when the area actually has
                    a label — uncategorized items (no areaId/areaLabel at all)
                    render without a header, same as QuoteDocument.js's
                    `{group.areaLabel && (...)}` guard. */}
                {group.areaLabel && (
                  <TableRow className="bg-gray-50">
                    <TableCell colSpan={5} className="font-semibold text-xs uppercase tracking-wide text-gray-500 py-2">
                      {group.areaLabel}
                    </TableCell>
                  </TableRow>
                )}
                {group.items.map((item) => {
              const idx = lineItems.indexOf(item);
              const itemId = item.id || `item-${idx}`;
              const isSelected = selectedItems.includes(itemId);
              // PT-27 27.4 (2026-07-24): line renders `{area} — {sub-area} · {product}` per JOB-1
              // §118/§167 wire — area + sub-area BOLD, product plain. Degrades cleanly when parts
              // are missing: (a+s+p), (a+s), (a+p), (s+p), (p only). Defensive-read snake+camel.
              const areaLabelRaw = item.areaLabel || item.area_label || '';
              const subAreaLabelRaw = item.subAreaLabel || item.sub_area_label || '';
              // IDI-40 / IDI-536: the shared Quote Items sq-ft reader
              // (utils/lineQuantity); the Create WO sheet reads it too.
              const sqFt = lineSqFt(item);
              // R20-7 (corrected): a line item may appear on any number of WOs.
              // The lock is removed — covered rows are still annotated (shows which
              // WO already has this item) but NOT disabled or dimmed.
              const coveringWO = coverageMap[String(itemId)];
              // IDI-411: the covering WO by its number or title, never its id.
              const coveringWoLabel = coveringWO ? workOrderLabel(coveringWO) : null;
              return (
                <TableRow
                  key={itemId}
                  className={clsx(
                    isSelected && 'bg-[color:var(--color-bg-selected)]'
                  )}
                >
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onToggleItem(itemId)}
                      aria-label={coveringWO ? `Also on ${coveringWoLabel}` : 'Select quote item'}
                    />
                  </TableCell>
                  <TableCell className="w-40">
                    <span className="text-sm truncate block" title={areaLabelRaw || undefined}>
                      {areaLabelRaw || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="w-40">
                    <span className="text-sm truncate block" title={subAreaLabelRaw || undefined}>
                      {subAreaLabelRaw || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="min-w-0">
                    <div className="truncate">
                      <span className="text-sm">{getLineItemName(item) || "—"}</span>
                      {coveringWO && (
                        <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          · on {coveringWoLabel}
                        </span>
                      )}
                    </div>
                    {(item.notes || item.note) && (
                      <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-muted)' }} title={item.notes || item.note}>
                        {item.notes || item.note}
                      </div>
                    )}
                  </TableCell>
                  <TableCell align="right" className="w-24">{sqFt != null ? sqFt : '—'}</TableCell>
                </TableRow>
              );
                })}
              </React.Fragment>
            ))}

          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

// IDI-478: job-level rows for the golden card — job type and the contract
// total (hidden from field techs, IDI-523). Never shows a raw id; "—" when a
// value is missing. IDI-839: no Quote row — once converted, the job is the
// project. The quote number stays on the Files, Notes and Acknowledgments
// tabs (the quote's own files, notes and events).
export function buildJobRows(job, { getEnumLabel, isFieldTech } = {}) {
  if (!job) return [];
  const typeId = job.jobTypeId || job.job_type_id;
  let jobType = job.jobType || job.job_type || '';
  if (!jobType && typeId && getEnumLabel) {
    const label = getEnumLabel('job_types', typeId);
    // getEnumLabel echoes an unmatched value back; that would be the id.
    jobType = label && String(label).trim().toLowerCase() !== String(typeId).trim().toLowerCase() ? label : '';
  }
  const rows = [
    { label: 'Job type', value: jobType || '—' },
  ];
  if (!isFieldTech) {
    const money = getJobContractTotal(job);
    // IDI-560: the hover shows the discount (when there is one) between
    // subtotal and tax.
    const parts = money && money.subtotalCents != null && money.taxCents != null
      ? [
        `Subtotal ${formatCurrency(money.subtotalCents)}`,
        money.discountCents > 0 ? `Discount ${formatCurrency(money.discountCents)}` : null,
        `Tax ${formatCurrency(money.taxCents)}`,
      ].filter(Boolean).join(' · ')
      : undefined;
    rows.push({
      label: 'Contract total',
      value: money ? formatCurrency(money.totalCents) : '—',
      title: parts,
    });
  }
  return rows;
}

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // IDI-157: Back = the page (and tab) the user came from — carried through
  // ConvertedQuoteGuard when the job was reached via its quote; with no
  // in-app origin, the section default. Captured once at mount: the
  // ?tab= sync below navigates without state and would otherwise drop it.
  const [back] = useState(() => resolveBack(location.state, { href: '/jobs', label: 'Back to Jobs' }));
  const { user } = useAuth();
  const toast = useToast();
  const { requestComplete, requestReopen, dialog: completionDialog } = useJobCompletion();
  // IDI-523 D3: the server's viewer rule (groups, else role).
  const isFieldTech = isFieldTechRule(user);
  // Job Details card resolves the job type's label (IDI-478): consumed by
  // buildJobRows(...) in the render below. The IDI-551 merge (PR #625) left
  // that reference without this declaration → ReferenceError on every render.
  // useSettingsContext() always returns an object (no-provider fallback).
  const { getEnumLabel } = useSettingsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(
    // IDI-566: ?tab= first, then a caller's navigation state. A state-derived
    // tab is written into ?tab= on mount by the activeTab sync effect below,
    // so the URL is canonical and survives a reload.
    () => {
      const asked = searchParams.get('tab') || location.state?.tab || 'work-orders';
      return isFieldTech && !FIELD_TECH_TABS.includes(asked) ? 'work-orders' : asked;
    }
  );
  // IDI-523: a field tech never lands on a hidden tab (e.g. the role resolves
  // after mount); the ?tab= sync effect below then rewrites the URL.
  useEffect(() => {
    if (isFieldTech && !FIELD_TECH_TABS.includes(activeTab)) setActiveTab('work-orders');
  }, [isFieldTech, activeTab]);
  const schedulingDefaults = useSchedulingDefaults();

  // Data for each tab
  const [workOrders, setWorkOrders] = useState([]);
  const { items: crewList } = useSettingsList('crews');
  // PT-22 W3-fp1 F0: read from the authoritative Settings bucket
  // ('resource_types') — the previous 'resources' key was disjoint from where
  // Settings writes. See PT-22 W3 W3-BUG-2 for the full story.
  const { items: resourceList } = useSettingsList('resource_types');
  // PT-22 W2: field-tech options for the WOCreateSheet Assign toggle's tech
  // branch. This is a READ over existing users — not the E5 deferral (which
  // is the '+ Invite tech' CREATE CTA, still omitted).
  const { options: techOptions } = useTechOptions();
  const [changeOrders, setChangeOrders] = useState([]);
  const [notes, setNotes] = useState([]);
  // IDI-551: the converted quote's notes (server-computed on the job GET).
  const [quoteNotes, setQuoteNotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  // IDI-55: the converted quote's files (server-computed on the job GET).
  const [quoteDocuments, setQuoteDocuments] = useState([]);
  // PT-53b commit 1: AttachModal state. CTA is flag-gated inside FilesTab.
  const [attachOpen, setAttachOpen] = useState(false);
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [customer, setCustomer] = useState(null);

  // Side-sheet state
  const [showWOSheet, setShowWOSheet] = useState(false);
  const [showCOSheet, setShowCOSheet] = useState(false);

  // CO notification dot
  const pendingCOs = changeOrders.filter(co =>
    co.status === 'sent' || co.status === 'accepted'
  ).length;

  // PT-19 CLEANUP-6 (2026-07-24): coverage map for the Quote Items panel per JOB-1
  // §168 / JOB-08. Maps every line-item id claimed by ANY existing WO → that WO.
  // Used to dim + lock covered rows in the panel; defensive-read snake_case+camelCase.
  const coverageMap = useMemo(() => {
    const map = {};
    workOrders.forEach((wo) => {
      const ids = Array.isArray(wo.lineItemIds) ? wo.lineItemIds
        : Array.isArray(wo.line_item_ids) ? wo.line_item_ids
        : [];
      ids.forEach((liId) => { if (liId != null) map[String(liId)] = wo; });
    });
    return map;
  }, [workOrders]);

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Runs on mount too: this is what puts a location.state.tab (IDI-566) into ?tab=.
  useEffect(() => {
    setSearchParams({ tab: activeTab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // IDI-522/IDI-551: re-read the job's notes and its quote's notes as the
  // Notes tab opens (as IDI-55 does for files), so a note added since this
  // page loaded shows up. Quiet refresh: no skeleton, errors ignored.
  useEffect(() => {
    if (activeTab !== 'notes' || !id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getJob(id);
        if (cancelled || !data) return;
        setNotes(Array.isArray(data.notes) ? data.notes : []);
        setQuoteNotes(Array.isArray(data.quoteNotes) ? data.quoteNotes : []);
        setWorkOrders(data.work_orders || data.workOrders || []);
      } catch {
        // keep what the page loaded with
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, id]);

  // IDI-55: a job/invoice/quote row was deleted; drop it locally.
  const handleFileDeleted = (row) => {
    const attId = row?.doc?.attachmentId;
    if (!attId) return;
    if (row.sourceKind === 'quote') setQuoteDocuments((prev) => removeDocumentById(prev || [], attId));
    else setDocuments((prev) => removeDocumentById(prev || [], attId));
  };

  const mergedNotes = useMemo(
    () => mergeJobNotes(notes, quoteNotes, job),
    [notes, quoteNotes, job],
  );

  const fetchJob = async () => {
    try {
      setLoading(true);
      const data = await api.getJob(id);
      setJob(data);
      setWorkOrders(data.work_orders || data.workOrders || []);
      // DEF-159 (2026-08-04): fetch COs from the dedicated endpoint.
      // The job detail response does NOT include change_orders (the Go
      // handler doesn't embed them). They live in a separate table with
      // their own list endpoint: GET /v1/jobs/{jobId}/change-orders.
      // IDI-582 S4: a field tech can't read change orders (the API refuses),
      // so none are fetched and the Files tab has no change-order rows.
      if (isFieldTech) {
        setChangeOrders([]);
      } else {
        try {
          const coRes = await api.getChangeOrders(id);
          const coArr = Array.isArray(coRes) ? coRes : coRes?.data || coRes?.change_orders || [];
          setChangeOrders(coArr);
        } catch {
          setChangeOrders(data.change_orders || data.changeOrders || []);
        }
      }
      setNotes(data.notes || []);
      setQuoteNotes(Array.isArray(data.quoteNotes) ? data.quoteNotes : []);
      setDocuments(data.documents || []);
      setQuoteDocuments(Array.isArray(data.quoteDocuments) ? data.quoteDocuments : []);
      // Fetch customer details for the persistent customer card (PT-39b).
      const custId = data.customer_id || data.customerId;
      if (custId) {
        try {
          const custData = await api.getCustomer(custId);
          setCustomer(custData);
        } catch (e) {
          // Non-critical — card will show job-level data only
        }
      }
    } catch (err) {
      setError('Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  // IDI-552: the author edits/deletes a quote note from the job's Notes tab.
  // The quote endpoint returns the quote's notes list, which replaces
  // quoteNotes (the job's own notes are untouched).
  const handleEditQuoteNote = async (note, content) => {
    try {
      const list = await api.editQuoteNote(note.quoteId, note.noteId, content);
      const out = list?.internal_notes ?? list;
      if (Array.isArray(out)) setQuoteNotes(out);
    } catch (err) {
      toast.error(noteActionErrorMessage(err, 'save'));
      throw err;
    }
  };
  const handleDeleteQuoteNote = async (note) => {
    try {
      const list = await api.deleteQuoteNote(note.quoteId, note.noteId);
      const out = list?.internal_notes ?? list;
      if (Array.isArray(out)) setQuoteNotes(out);
    } catch (err) {
      toast.error(noteActionErrorMessage(err, 'delete'));
      throw err;
    }
  };

  // IDI-553: the author edits/deletes their own job note. The server returns
  // the saved job; its notes replace ours (an edited note re-sorts to the top
  // via mergeJobNotes' updated_at ordering).
  const handleEditJobNote = async (note, content) => {
    try {
      const saved = await api.editNote(id, note.id, content);
      if (saved && Array.isArray(saved.notes)) setNotes(saved.notes);
      else fetchJob();
    } catch (err) {
      toast.error(noteActionErrorMessage(err, 'save'));
      throw err;
    }
  };
  const handleDeleteJobNote = async (note) => {
    try {
      const saved = await api.deleteNote(id, note.id);
      if (saved && Array.isArray(saved.notes)) setNotes(saved.notes);
      else fetchJob();
    } catch (err) {
      toast.error(noteActionErrorMessage(err, 'delete'));
      throw err;
    }
  };

  const handleAddNote = async (content) => {
    try {
      // IDI-305 / PT-40.7: attribute the note to the signed-in user, as
      // WorkOrderDetail does. IDI-522/IDI-551: only the job's own notes are
      // written; quote notes are read from the quote and never written here.
      // IDI-453: one add_job_note action. The server stamps the id and the
      // real author (never "You") on the fresh job, so two people adding
      // notes from stale tabs both keep theirs.
      const saved = await api.addJobNote(id, content);
      if (saved && Array.isArray(saved.notes)) setNotes(saved.notes);
      else fetchJob();
    } catch (err) {
      // QA: rethrow so NotesPanel keeps the typed text (it clears the input
      // only when onAdd resolves); the failure is reported here.
      const status = err?.response?.status ?? err?.status;
      toast.error(status === 409
        ? 'This job was changed by someone else. Reload and try again. Your note is still in the box.'
        : 'Failed to add note. Your note is still in the box.');
      throw err;
    }
  };

  const handleCreateWO = () => {
    setShowWOSheet(true);
  };

  // IDI-246: complete or reopen through the confirm. The saved job comes back
  // as a GET reads it, so the pill, work orders and Notes all refresh at once.
  const jobCompleted = isJobCompleted(job);
  const handleCompleteOrReopen = async () => {
    const res = jobCompleted ? await requestReopen(job) : await requestComplete(job);
    if (!res) return;
    const saved = res.ok[0];
    if (saved && typeof saved === 'object' && saved.id) {
      setJob(saved);
      setWorkOrders(saved.work_orders || saved.workOrders || []);
      if (Array.isArray(saved.notes)) setNotes(saved.notes);
    } else if (res.ok.length) {
      fetchJob();
    }
    if (res.failed.length) {
      toast.error(apiErrorMessage(res.failed[0].err, jobCompleted ? 'Failed to reopen the job.' : 'Failed to complete the job.'));
    }
  };

  const handleCreateCO = () => {
    setShowCOSheet(true);
  };

  const handleUploadDoc = () => {
    // PT-19 19.8 (2026-07-24): DEFERRED. PT-53b commit 1 (2026-08-02): the
    // getUploadUrl handler is now cross-scope (commit f4ccc99); the AttachModal
    // is built + unit-tested; the CTA is gated behind FLAGS.ATTACHMENTS_UPLOAD
    // until S3_BUCKET_FILES lands on the deployed Lambda (CTO note pending).
    // When flag is on, this opens the modal instead of no-op.
    setAttachOpen(true);
  };

  // PT-53b commit 2 (2026-08-03): the client-side ref-write to job.documents[]
  // was moved SERVER-SIDE into `createAttachment` (backend-go/cmd/quotes/attachments.go).
  // The transactional write there guarantees the Attachment record + the
  // job.documents[] entry land atomically — closes the two-write race that
  // was the operator's blocking condition on FLAGS.ATTACHMENTS_UPLOAD.
  //
  // What this handler still does: mirror the ref into local state for an
  // instant UI update, so the user doesn't wait on a re-fetch to see their
  // file appear. The server is now the durable truth AND the atomic writer;
  // if createAttachment's transaction failed, AttachModal surfaces the
  // error and this handler is never called.
  const handleAttached = (ref) => {
    // ref shape: { attachmentId, source, filename, contentType, sizeBytes, attachedAt }
    setDocuments((prev) => [...(prev || []), ref]);
  };

  if (loading) return <div className="space-y-4 px-6"><SkeletonCard /><SkeletonCard /></div>;

  if (!job) {
    return (
      <div className="px-6">
        <Alert variant="error">Job not found</Alert>
        {/* §3a (2026-08-08): migrated hand-rolled Back button to canonical BackLink atom. */}
        <div className="mt-4">
          <BackLink to={back.href} state={back.state} label={back.label} />
        </div>
      </div>
    );
  }

  const lineItems = job.line_items || job.lineItems || [];

  return (
    <div className="px-6">
      {/* PT-18 (2026-07-24): DetailPageHeader replaces the legacy PageHeader per
          JOB-1 §165/§166. Title = project name, subtitle = job number, status pill
          inline (normalized so legacy 'in_progress' → 'partially_scheduled'). Actions
          slot renders a Create ▾ dropdown (JOB-1 §171) — no top-level Edit, no
          overflow kebab. Scope changes flow through Change Order. */}
      <DetailPageHeader
        title={job.title || 'Untitled Job'}
        subtitle={formatJobNumber(job)}
        backHref={back.href}
        backState={back.state}
        backLabel={back.label}
        // IDI-139 §2 (2026-09-03, Adrian's ruling): derive from workOrders[].startAt.
        status={deriveJobStatus(job)}
        pills={
          // IDI-523: the pill carries the locked total; no field tech sees it.
          !isFieldTech && (job.acceptedConfigurationId || job.accepted_configuration_id)
            ? (
              <LockedAtAcceptancePill
                lockedTotalCents={job.lockedTotalCents ?? job.locked_total_cents}
                acceptedConfigurationId={job.acceptedConfigurationId || job.accepted_configuration_id}
                quoteId={job.quoteId || job.quote_id}
              />
            )
            : undefined
        }
        actions={isFieldTech ? null : (
          <div className="flex items-center gap-2">
          {/* IDI-246: Complete Job, or Reopen Job when Completed, left of Create. */}
          <Button variant="secondary" onClick={handleCompleteOrReopen}>
            {jobCompleted ? 'Reopen Job' : 'Complete Job'}
          </Button>
          <DropdownMenu
            ariaLabel="Create"
            trigger={
              <Button variant="primary">
                <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                Create
                <ChevronDownIcon className="h-4 w-4 ml-1" aria-hidden="true" />
              </Button>
            }
            items={[
              {
                label: 'Work Order',
                icon: <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />,
                onClick: handleCreateWO,
              },
              {
                label: 'Change Order',
                icon: <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />,
                onClick: handleCreateCO,
              },
              {
                label: 'Invoice',
                icon: <BanknotesIcon className="h-4 w-4" aria-hidden="true" />,
                onClick: () => navigate(`/jobs/${id}/invoices/new`),
              },
            ]}
          />
          </div>
        )}
      />
      {completionDialog}

      {/* Error */}
      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Customer detail card — persists across all tabs (REQ-WF-04). PT-39b
          (2026-07-27): retired EntityDetailCard in favour of the canonical
          GoldenCustomerCard atom with `surface="job"`. Overflow sheets
          (contacts / addresses / salespeople), status chip, and copy
          affordances now flow through the shared atom — no more parallel
          detail-card implementations.

          Data model: GoldenCustomerCard reads from a single `customer`
          object. On JobDetail the raw customer isn't guaranteed loaded on
          first render (fetched async after the job payload), so we build a
          `mergedCustomer` that layers job-side fallbacks under the real
          customer's fields. The one deliberate OVERRIDE, not a fallback,
          is `address`: on a Job Detail page the address to display is the
          JOB SITE — not the customer's default. Resolved via
          getJobSiteAddress (utils/customerAddress) which absorbs the
          precedence chain that used to live inline here (PR-73 Commit 3,
          dispatch 433 §2). Everything else prefers the loaded customer
          when available. */}
      <GoldenCustomerCard
        surface="job"
        customer={{
          ...customer,
          // Fallbacks so the card renders SOMETHING on the pre-load frame.
          name: customer?.name || job.customer_name || job.customerName || 'Customer',
          company: customer?.company || job.company,
          phone: customer?.phone || job.customer_phone || job.customerPhone,
          email: customer?.email || job.customer_email || job.customerEmail,
          // Job-site override: PR-73 Commit 3 (dispatch 433 §2) migrated the
          // inline precedence chain that lived here to
          // utils/customerAddress.js::getJobSiteAddress. Do NOT re-inline
          // the chain — PORTICA-89 tracks the alias-scatter class this
          // helper is closing. If you need to add a spelling, add it in
          // the helper's chain (with a test) and leave this line alone.
          //
          // Reader is LIVE against the customer today; PORTICA-25 tracks
          // converting per-level address copies to snapshots.
          address: getJobSiteAddress({ job, customer }),
        }}
        onEdit={customer?.id ? () => navigate(`/customers/${customer.id}/edit`) : undefined}
        jobRows={buildJobRows(job, { getEnumLabel, isFieldTech })}
        className="mb-6"
      />

      {/* PT-27 27.1 (2026-07-24): the standalone `<QuoteLineItemsPanel>` that used
          to render here was moved INSIDE the Work Orders tab per JOB-1 §111/§101.
          Selection state stays hoisted (used by the snackbar). */}

      {/* PT-19 19.1 + 19.2 (2026-07-24): tab strip migrated to shared <Tabs> primitive.
          - Order matches JOB-1 §172.
          - Change Orders tab gets a red dot when pendingCOs > 0 (new `dot` prop on the atom).
          Retains `activeTab` state + ?tab URL param sync (see effect above). */}
      <Tabs
        tabs={(isFieldTech ? TABS.filter((t) => FIELD_TECH_TABS.includes(t.value)) : TABS).map((t) => {
          // PT-19 CLEANUP-4 (2026-07-24): per-tab counts per JOB-1 §101/§106.
          // Change Orders shows the PENDING count (pendingCOs), not the total —
          // matches the wire's "attention" semantics for that tab specifically.
          let count;
          switch (t.value) {
            case 'work-orders':   count = workOrders.length;   break;
            case 'notes':         count = mergedNotes.length; break;
            // IDI-477/IDI-55: the badge counts the rows the Files tab shows.
            case 'documents':     count = buildJobFileRows({
              documents, quoteDocuments, workOrders, changeOrders,
            }).length; break;
            case 'change-orders': count = pendingCOs;          break;
            case 'invoices':      count = Array.isArray(job.invoices) ? job.invoices.length : undefined; break;
            case 'costing':       count = undefined;           break;
            default:              count = undefined;
          }
          return {
            ...t,
            count,
            dot: t.value === 'change-orders' && pendingCOs > 0,
          };
        })}
        value={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {/* Tab Content */}
      <div>
        {activeTab === 'work-orders' && (
          <>
            {/* IDI-274 (2026-09-08): Work Orders card renders ABOVE the Quote Items
                panel. This reverses the prior JOB-1 §111/§101 order ("Above the WO list")
                per Adrian's 2026-09-08 direction; see IDI-274. Selection state stays
                hoisted at page level (:485) so the snackbar (:891) still reads it. */}
            <WorkOrdersTab
              workOrders={workOrders}
              jobId={id}
              onCreateWO={handleCreateWO}
              isFieldTech={isFieldTech}
              crews={crewList}
              canDelete={!!user?.role && !isFieldTech}
              onDeleted={fetchJob}
              onError={setError}
            />
            <QuoteLineItemsPanel
              lineItems={lineItems}
              selectedItems={selectedLineItems}
              coverageMap={coverageMap}
              onToggleItem={(itemId) => {
                setSelectedLineItems(prev =>
                  prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
                );
              }}
              onToggleAll={() => {
                const selectableIds = lineItems
                  .map((item, idx) => item.id || `item-${idx}`)
                  .filter((id) => !coverageMap[String(id)]);
                if (selectableIds.length > 0 && selectableIds.every((id) => selectedLineItems.includes(id))) {
                  setSelectedLineItems([]);
                } else {
                  setSelectedLineItems(selectableIds);
                }
              }}
            />
          </>
        )}
        {activeTab === 'change-orders' && (
          <ChangeOrdersTab
            changeOrders={changeOrders}
            jobId={id}
            onCreateCO={handleCreateCO}
          />
        )}
        {activeTab === 'notes' && (
          <NotesTab
            notes={mergedNotes}
            onAddNote={handleAddNote}
            onEditQuoteNote={handleEditQuoteNote}
            onDeleteQuoteNote={handleDeleteQuoteNote}
            onEditJobNote={handleEditJobNote}
            onDeleteJobNote={handleDeleteJobNote}
            currentUserId={user?.id || null}
          />
        )}
        {activeTab === 'invoices' && (
          <InvoicesTab jobId={id} />
        )}
        {activeTab === 'costing' && (
          <JobCostingTab
            job={job}
            workOrders={workOrders}
            onUpdateMaterialActual={async (row, actualUsed) => {
              try {
                await api.updateJob(id, { material_actuals: [{ key: row.key, actual_used: actualUsed }] });
                fetchJob();
              } catch (err) {
                setError('Failed to save material actuals');
              }
            }}
            onUpdateWOLabor={async (wo, hours) => {
              try {
                // Work orders live embedded in job.work_orders[] (no standalone
                // WO table/API) — persist labor hours through the job record.
                // IDI-453: per-WO save (page state may be stale).
                await api.updateWorkOrder(id, wo.id || wo.workOrderId, { actualHours: hours });
                fetchJob();
              } catch (err) {
                setError(err?.response?.status === 409 ? err.friendlyMessage : 'Failed to save labor hours');
              }
            }}
          />
        )}
        {activeTab === 'documents' && (
          <FilesTab
            jobId={id}
            documents={documents}
            quoteDocuments={quoteDocuments}
            workOrders={workOrders}
            changeOrders={changeOrders}
            quoteNumber={job.quoteNumber ?? job.quote_number}
            onUpload={handleUploadDoc}
            attachEnabled={isEnabled('ATTACHMENTS_UPLOAD')}
            isFieldTech={isFieldTech}
            userId={user?.id || null}
            onDeleted={handleFileDeleted}
          />
        )}
        {/* IDI-542: the job's acknowledgment history — the quote's rows plus
            its change orders'; the document column says which. */}
        {activeTab === 'acknowledgements' && (
          <Card>
            <div className="p-4">
              <AcknowledgmentsPanel documentType="job" documentId={id} />
            </div>
          </Card>
        )}
      </div>

      {/* WO Create Side-Sheet */}
      {/* PT-19 19.6 + 19.7 + CLEANUP-7 (2026-07-24): floating snackbar migrated to shared
          BulkActionBar. Per JOB-1 §171 + operator ruling: Create Work Order + clear
          only — the "Add to Existing WO" branch is REMOVED (scope changes go through
          the Change Order flow). variant="primary" (net-new on the atom, CLEANUP-7)
          renders as the solid-red primary pill per the wire; the atom's built-in `×`
          provides the clear affordance. */}
      {/* PT-27 27.5 (2026-07-24): dismiss snackbar when a side sheet is open per
          JOB-1 §171. Selection state stays populated so cancelling the sheet
          restores the snackbar with the same rows selected. */}
      {activeTab === 'work-orders' && !showWOSheet && !showCOSheet && (
        <BulkActionBar
          count={selectedLineItems.length}
          onClear={() => setSelectedLineItems([])}
          actions={[
            {
              label: 'Create Work Order',
              variant: 'primary',
              onClick: () => setShowWOSheet(true),
            },
          ]}
        />
      )}

      <WOCreateSheet
        creator={user}
        open={showWOSheet}
        onClose={() => setShowWOSheet(false)}
        onSubmit={async (woData) => {
          try {
            // No standalone WO table/API — append the new work order to the
            // job's embedded work_orders[] array.
            const newWO = {
              id: woData.id || `wo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              ...woData,
            };
            // IDI-453: append server-side; never PUT a list built from page state.
            await api.createWorkOrder(id, newWO);
            setSelectedLineItems([]); // Dismiss multi-select bar after creation (REQ-WF-07)
            fetchJob(); // Refresh to show new WO
          } catch (err) {
            setError(err?.response?.status === 409 ? err.friendlyMessage : 'Failed to create work order');
          }
        }}
        job={job}
        crews={crewList}
        resources={resourceList}
        techs={techOptions}
        existingWorkOrders={workOrders}
        schedulingDefaults={schedulingDefaults}
        preCheckedLineItemIds={selectedLineItems}
        onViewConflict={(woId) => navigate(`/work-orders/${encodeURIComponent(woId)}?job=${encodeURIComponent(id)}`, { state: { jobId: id } })}
      />

      {/* CO Create Side-Sheet */}
      <COCreateSheet
        open={showCOSheet}
        onClose={() => setShowCOSheet(false)}
        onSubmit={async (coData) => {
          try {
            const created = await api.createChangeOrder(coData);
            const coId = created?.id || created?.data?.id;
            if (coId) {
              navigate(`/jobs/${id}/change-orders/${coId}/build`);
            } else {
              fetchJob();
            }
          } catch (err) {
            setError('Failed to create change order');
          }
        }}
        job={job}
      />

      {/* PT-53b commit 1: cross-scope attach modal for the Files tab.
        * Owner-carries-references per FILES_SCOPE_AND_VISIBILITY.md §4b — the
        * modal calls onAttached with an AttachmentRef, and handleAttached
        * appends to job.documents[] via api.updateJob. Rendered unconditionally
        * (the modal is closed when attachOpen=false and the CTA is flag-gated),
        * so the JSX shape is stable across flag flips.
        */}
      <AttachModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        scope="job"
        ownerId={id}
        ownerLabel={job?.title || formatJobNumber(job)}
        companyId={user?.tenantId}
        onAttached={handleAttached}
      />
    </div>
  );
};

export default JobDetail;
